import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  PLANS,
  PAID_PLAN_IDS,
  planPrice,
  GST_RATE,
  TRIAL_DAYS,
  type BillingInterval,
  type Currency,
  type PlanId,
} from '@captionflow/shared';
import { api, ApiClientError } from '../api/client';
import { Logo } from '../components/Logo';
import '../styles/billing.css';

function money(amount: number, currency: Currency): string {
  return currency === 'INR' ? `₹${amount.toLocaleString('en-IN')}` : `$${amount}`;
}

export function PricingPage() {
  const nav = useNavigate();
  const [interval, setInterval] = useState<BillingInterval>('monthly');
  const [busy, setBusy] = useState<PlanId | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Best-effort: know the signed-in user's currency + current plan. Ignored (and
  // the page still renders in USD) when logged out.
  const { data: billing } = useQuery({
    queryKey: ['billing-me'],
    queryFn: () => api.getBilling(),
    retry: false,
    staleTime: 30_000,
  });
  const currency: Currency = billing?.entitlements.currency ?? 'USD';
  const currentPlan = billing?.entitlements.plan;
  const loggedIn = Boolean(billing);

  const startCheckout = async (planId: Exclude<PlanId, 'free'>) => {
    setError(null);
    if (!loggedIn) {
      nav('/login');
      return;
    }
    setBusy(planId);
    try {
      const { url } = await api.checkout(planId, interval);
      window.location.href = url;
    } catch (e) {
      setError(
        e instanceof ApiClientError && e.code === 'billing_unconfigured'
          ? 'Payments aren’t switched on in this environment yet.'
          : e instanceof ApiClientError
            ? e.message
            : 'Could not start checkout. Please try again.',
      );
      setBusy(null);
    }
  };

  const order: PlanId[] = ['free', ...PAID_PLAN_IDS];

  return (
    <div className="pay">
      <div className="pay-shell">
        <div className="pay-top">
          <Link to="/" aria-label="Kaption home"><Logo size={20} /></Link>
          <Link to="/" className="pay-back">← Back</Link>
        </div>

        <div className="pay-head">
          <h1>Simple pricing that scales with you</h1>
          <p>Start free, no card. Upgrade when you need more minutes, kMagic, and 1080p.</p>
          <div className="pay-center">
            <div className="pay-toggle" role="tablist" aria-label="Billing interval">
              <button className={interval === 'monthly' ? 'on' : ''} onClick={() => setInterval('monthly')}>
                Monthly
              </button>
              <button className={interval === 'annual' ? 'on' : ''} onClick={() => setInterval('annual')}>
                Annual<span className="pay-save">1 month free</span>
              </button>
            </div>
          </div>
        </div>

        <div className="pay-grid">
          {order.map((id) => {
            const plan = PLANS[id];
            const price = planPrice(id, interval);
            const isPaid = id !== 'free';
            const isCurrent = currentPlan === id;
            const pop = id === 'creator';
            return (
              <div key={id} className={pop ? 'pay-card pop' : 'pay-card'}>
                {pop && <span className="pay-badge">Most popular</span>}
                <h3>{plan.name}</h3>
                <p className="pay-tag">{plan.tagline}</p>
                <div className="pay-price">
                  {price ? (
                    <>
                      <b>{money(currency === 'INR' ? price.inr : price.usd, currency)}</b>
                      <span>/{interval === 'annual' ? 'yr' : 'mo'}</span>
                    </>
                  ) : (
                    <b>Free</b>
                  )}
                </div>
                <p className="pay-gst">
                  {isPaid
                    ? currency === 'INR'
                      ? `incl. ${Math.round(GST_RATE * 100)}% GST`
                      : 'Billed in USD'
                    : `${TRIAL_DAYS}-day trial, no card`}
                </p>
                <ul className="pay-feats">
                  {plan.features.map((f) => (
                    <li key={f}>{f}</li>
                  ))}
                </ul>
                {isCurrent ? (
                  <button className="pay-btn current" disabled>Current plan</button>
                ) : isPaid ? (
                  <button
                    className={pop ? 'pay-btn fill' : 'pay-btn'}
                    disabled={busy === id}
                    onClick={() => startCheckout(id as Exclude<PlanId, 'free'>)}
                  >
                    {busy === id ? 'Starting…' : `Choose ${plan.name}`}
                  </button>
                ) : (
                  <Link to={loggedIn ? '/' : '/login'} className="pay-btn">
                    {loggedIn ? 'Go to dashboard' : 'Start free'}
                  </Link>
                )}
              </div>
            );
          })}
        </div>

        {error && <p className="pay-error">{error}</p>}
      </div>
    </div>
  );
}
